import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ShippingGuidePage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Article Hero */}
      <section className="relative w-full pt-20 pb-8 md:pt-24 md:pb-12 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
            <Link to="/" className="hover:text-foreground-800 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-[10px]"></i>
            <span className="text-foreground-400">Guides</span>
            <i className="ri-arrow-right-s-line text-[10px]"></i>
            <span className="text-foreground-700 font-medium">Shipping Guide</span>
          </nav>

          <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium tracking-wide mb-4">
            Shipping &amp; Logistics
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-foreground-950 leading-tight mb-4">
            Superbuy Spreadsheet Shipping Guide: Costs, Carriers &amp; Customs 2026
          </h1>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
            The definitive international shipping companion for Superbuy Spreadsheet shoppers. Compare carriers, estimate costs, navigate customs, consolidate orders, and choose the smartest route for your location — from the US and UK to Europe, Australia, and beyond.
          </p>
          <div className="flex items-center gap-4 mt-6 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i> 11 min read
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-global-line"></i> Covers 5+ Major Regions
            </span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed space-y-8">

          {/* Section 1: Shipping Overview */}
          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              Why Shipping Strategy Matters for Superbuy Spreadsheet Shoppers
            </h2>
            <p className="text-sm md:text-base leading-relaxed">
              Finding amazing products through the Superbuy Spreadsheet is only half the battle. Getting those items from the seller&rsquo;s warehouse to your doorstep — across international borders, through customs checkpoints, and via multiple carrier handoffs — is where careful planning pays off enormously. A poorly chosen shipping method can double your total cost or add weeks of unnecessary waiting. A smart shipping strategy, on the other hand, can save you hundreds of dollars per year and ensure your packages arrive reliably every time.
            </p>
            <p className="text-sm md:text-base leading-relaxed">
              This guide is built specifically for Superbuy Spreadsheet users. We cover the shipping ecosystem from the perspective of someone who discovers products through the spreadsheet and then purchases them through international platforms. If you are completely new to the entire process, start with our <Link to="/superbuy-spreadsheet-guide" className="text-primary-600 hover:text-primary-700 font-medium underline underline-offset-4">Complete Beginner Guide</Link> first, then come back here to master the logistics side.
            </p>
          </section>

          {/* Section 2: Shipping Methods */}
          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              Understanding the Main Shipping Methods
            </h2>
            <p className="text-sm md:text-base leading-relaxed">
              International shipping platforms typically offer several tiers of service, each with its own trade-offs between speed, cost, and reliability. Here is how the main options compare.
            </p>

            <div className="overflow-x-auto my-6">
              <table className="w-full text-xs md:text-sm border-collapse">
                <thead>
                  <tr className="border-b border-background-300">
                    <th className="text-left py-3 pr-4 font-semibold text-foreground-900">Shipping Line</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground-900">Speed</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground-900">Cost</th>
                    <th className="text-left py-3 pl-4 font-semibold text-foreground-900">Best For</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-background-200">
                    <td className="py-3 pr-4 text-foreground-800 font-medium">Express (DHL, FedEx, UPS)</td>
                    <td className="py-3 px-4 text-foreground-700">3–7 days</td>
                    <td className="py-3 px-4 text-foreground-700">Highest</td>
                    <td className="py-3 pl-4 text-foreground-700">Urgent orders, high-value items</td>
                  </tr>
                  <tr className="border-b border-background-200">
                    <td className="py-3 pr-4 text-foreground-800 font-medium">EMS / ePacket</td>
                    <td className="py-3 px-4 text-foreground-700">7–20 days</td>
                    <td className="py-3 px-4 text-foreground-700">Medium</td>
                    <td className="py-3 pl-4 text-foreground-700">Small to medium packages, reliable tracking</td>
                  </tr>
                  <tr className="border-b border-background-200">
                    <td className="py-3 pr-4 text-foreground-800 font-medium">Air Parcel (Registered)</td>
                    <td className="py-3 px-4 text-foreground-700">10–30 days</td>
                    <td className="py-3 px-4 text-foreground-700">Medium-Low</td>
                    <td className="py-3 pl-4 text-foreground-700">Cost-effective with tracking</td>
                  </tr>
                  <tr className="border-b border-background-200">
                    <td className="py-3 pr-4 text-foreground-800 font-medium">Sea Freight / SAL</td>
                    <td className="py-3 px-4 text-foreground-700">30–90 days</td>
                    <td className="py-3 px-4 text-foreground-700">Lowest</td>
                    <td className="py-3 pl-4 text-foreground-700">Large hauls, non-urgent bulk orders</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4 text-foreground-800 font-medium">Agent Consolidation Line</td>
                    <td className="py-3 px-4 text-foreground-700">10–25 days</td>
                    <td className="py-3 px-4 text-foreground-700">Low-Medium</td>
                    <td className="py-3 pl-4 text-foreground-700">Multiple items from different sellers</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-sm md:text-base leading-relaxed">
              The key insight most experienced shoppers will tell you: <strong>express shipping is rarely worth the premium</strong> unless you genuinely need the items within a week. For the vast majority of Superbuy Spreadsheet finds — clothing, accessories, sneakers — the mid-tier options (EMS, registered air parcel, or agent consolidation) offer the best balance of speed, tracking visibility, and cost.
            </p>
          </section>

          {/* Section 3: Consolidation */}
          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              The Power of Order Consolidation
            </h2>
            <p className="text-sm md:text-base leading-relaxed">
              One of the biggest money-saving techniques for Superbuy Spreadsheet shoppers is <strong>order consolidation</strong>. Instead of shipping each item individually as you buy it, you accumulate multiple purchases at your agent&rsquo;s warehouse and then ship everything together in a single package.
            </p>
            <p className="text-sm md:text-base leading-relaxed">
              Here is a real-world example. Suppose you find five items from the Shoes, Hoodies, and Accessories categories in the spreadsheet. If you ship each item separately via EMS at approximately $25 per package, your total shipping cost would be $125. But if you consolidate all five items into one package, the combined weight might result in a single shipping charge of around $45 to $55 — a savings of over 50%.
            </p>
            <p className="text-sm md:text-base leading-relaxed">
              Consolidation is particularly effective when buying from multiple sellers. Many agents offer free storage for 30 to 90 days, giving you plenty of time to accumulate items before shipping. Just be mindful of storage deadlines and total package weight, as extremely heavy consolidated shipments can attract additional scrutiny from customs authorities.
            </p>
          </section>

          {/* Section 4: Customs */}
          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              Customs, Duties, and Import Taxes: What to Expect
            </h2>
            <p className="text-sm md:text-base leading-relaxed">
              Customs is the part of international shipping that intimidates most newcomers, but the reality is more manageable than it seems. Every country has its own de minimis threshold — the maximum value of goods that can be imported without triggering duties or taxes. Understanding your country&rsquo;s threshold is the single most important piece of customs knowledge you can have.
            </p>

            <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-6 mb-3">United States</h3>
            <p className="text-sm md:text-base leading-relaxed">
              The US has one of the most generous de minimis thresholds in the world at <strong>$800</strong>. This means most individual Superbuy Spreadsheet orders pass through customs completely duty-free. For orders exceeding $800, duties vary by product category but typically range from 0% to 20%. Clothing and footwear are generally at the lower end of that range.
            </p>

            <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-6 mb-3">United Kingdom</h3>
            <p className="text-sm md:text-base leading-relaxed">
              Since Brexit, the UK de minimis threshold is <strong>£135</strong>. For goods valued at £135 or less, VAT (currently 20%) is collected at the point of sale by the platform if they participate in the UK&rsquo;s VAT scheme. For goods over £135, both VAT and customs duty are due upon import. The UK system can be complex, so budgeting an extra 20–25% for larger orders is a safe practice.
            </p>

            <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-6 mb-3">European Union</h3>
            <p className="text-sm md:text-base leading-relaxed">
              The EU abolished its low-value consignment relief in 2021. All goods imported from outside the EU are now subject to VAT, typically at the rate of the destination country (generally 19–25%). The Import One-Stop Shop system means VAT is collected at checkout for participating platforms. Customs duties apply above €150. For EU shoppers, factoring VAT into your budget is essential — do not be surprised by it at delivery time.
            </p>

            <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-6 mb-3">Canada</h3>
            <p className="text-sm md:text-base leading-relaxed">
              Canada&rsquo;s de minimis threshold is <strong>C$20</strong> for most goods, which is quite low. Orders above this amount may be assessed GST/HST plus a handling fee. In practice, many packages under C$150 pass through without being assessed, but it is not guaranteed. Budget an extra 15–20% as a contingency.
            </p>

            <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-6 mb-3">Australia</h3>
            <p className="text-sm md:text-base leading-relaxed">
              Australia has a <strong>A$1,000</strong> de minimis threshold, which is very generous. Most individual Superbuy Spreadsheet orders clear customs without any duties or GST charges. For orders over A$1,000, duties and GST apply. The Australian Border Force provides clear online calculators to estimate your charges before purchasing.
            </p>
          </section>

          {/* Section 5: Cost Optimization */}
          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              Practical Cost-Saving Strategies
            </h2>
            <p className="text-sm md:text-base leading-relaxed">
              Beyond consolidation, there are several other proven strategies that experienced Superbuy Spreadsheet shoppers use to minimize shipping costs without sacrificing reliability.
            </p>

            <div className="space-y-3 my-6">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-background-100">
                <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-accent-600">1</span>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground-900 text-sm">Remove Unnecessary Packaging</h4>
                  <p className="text-xs md:text-sm text-foreground-700 leading-relaxed">Many agents offer a service to remove shoeboxes, excess wrapping, and bulky retail packaging before shipping. This can reduce volumetric weight by 20–40%, directly lowering your shipping cost.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-background-100">
                <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-accent-600">2</span>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground-900 text-sm">Time Your Shipments Around Holidays</h4>
                  <p className="text-xs md:text-sm text-foreground-700 leading-relaxed">Shipping costs spike during major holidays (Chinese New Year, Christmas season). Planning orders to ship in off-peak periods can save 10–20% while also reducing transit delays.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-background-100">
                <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-accent-600">3</span>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground-900 text-sm">Compare Agent Shipping Rates</h4>
                  <p className="text-xs md:text-sm text-foreground-700 leading-relaxed">Different agents have negotiated different rates with carriers. Spending an extra 10 minutes comparing shipping estimates across two or three agents can reveal significant differences, especially for heavier packages.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-background-100">
                <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-accent-600">4</span>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground-900 text-sm">Use Vacuum Sealing for Clothing</h4>
                  <p className="text-xs md:text-sm text-foreground-700 leading-relaxed">For clothing-heavy hauls — especially from the Hoodies, Jackets, and Pants categories — request vacuum sealing. This dramatically reduces volumetric weight and can cut shipping costs by 30% or more on bulky textile orders.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6: Tracking */}
          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              Tracking Your Package and Handling Delays
            </h2>
            <p className="text-sm md:text-base leading-relaxed">
              Once your consolidated package ships, tracking becomes your window into its journey. Most mid-tier and premium shipping lines provide end-to-end tracking with updates at every major checkpoint: origin warehouse departure, export customs clearance, airline handoff, destination country arrival, import customs clearance, local carrier handoff, and final delivery.
            </p>
            <p className="text-sm md:text-base leading-relaxed">
              It is completely normal for tracking to go silent for several days during international transit — especially when packages are on cargo flights or waiting in customs queues. Do not panic if you see no updates for 5 to 10 days. However, if tracking has been silent for more than 20 days, contact your agent or the carrier to initiate a trace. Most delayed packages are simply stuck in customs processing, not lost.
            </p>
          </section>

          {/* Section 7: FAQ */}
          <section>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
              Frequently Asked Shipping Questions
            </h2>
            <div className="space-y-5">
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm md:text-base mb-1">
                  How much should I budget for shipping on a typical order?
                </h3>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  A reasonable rule of thumb is <strong>15–25% of the product value</strong>. For a $200 order of mixed clothing items consolidated into one package, expect to pay roughly $30 to $50 for mid-tier shipping to the US or UK. Smaller, lighter items like accessories may cost less proportionally, while heavier items like jackets and shoes may push toward the higher end. Our <Link to="/superbuy-spreadsheet-categories" className="text-primary-600 hover:text-primary-700 font-medium underline underline-offset-4">Categories Guide</Link> includes typical shipping weight estimates by product type.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm md:text-base mb-1">
                  Should I buy insurance for my shipment?
                </h3>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  For orders under $100, the cost of insurance typically outweighs the benefit. For orders between $100 and $500, consider it if the shipping line has a reputation for occasional losses. For orders above $500, insurance is strongly recommended. Most shipping insurance costs 1–3% of the declared value — a small premium for complete peace of mind.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm md:text-base mb-1">
                  What happens if my package is seized by customs?
                </h3>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  Customs seizures of personal-use shipments from Superbuy Spreadsheet categories are <strong>extremely rare</strong>. Seizures typically only occur when prohibited items are shipped or when commercial quantities are involved. For personal clothing, sneakers, and accessories orders, the overwhelming majority clear customs without issues. If a seizure does occur, you will receive an official notice explaining the reason and your options for appeal or abandonment. See our <Link to="/superbuy-spreadsheet-safety-guide" className="text-primary-600 hover:text-primary-700 font-medium underline underline-offset-4">Safety Guide</Link> for more on what to avoid shipping.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm md:text-base mb-1">
                  Can I ship to a different address than my billing address?
                </h3>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  Yes, but with limitations. Most agents will ship to any address you provide. However, for your first few orders, some platforms may require the shipping address to match your verified billing address as a fraud prevention measure. After you establish a purchase history, shipping to alternate addresses — such as a workplace or a family member&rsquo;s home — is generally straightforward.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm md:text-base mb-1">
                  Which shipping line is best for my country?
                </h3>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  This varies significantly by destination. For the US, EMS and ePacket are reliable workhorses. For the UK, Royal Mail tracked services after customs clearance are generally smooth. For EU countries, DHL Express offers the fastest customs processing but at a premium. For Canada, EMS and registered air parcel are cost-effective. For Australia, EMS and Australia Post registered services are reliable. Always check recent community feedback for your specific route before choosing, as carrier performance can change seasonally.
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="rounded-lg bg-secondary-100/60 p-6 md:p-8 text-center my-10">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-3">
              Know Your Shipping, Love Your Haul
            </h2>
            <p className="text-sm md:text-base text-foreground-700 mb-5 max-w-lg mx-auto leading-relaxed">
              Great shipping is invisible — your items just show up. Apply these strategies and browse with confidence.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 rounded-full bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap"
              >
                Superbuy Sheet
                <i className="ri-arrow-right-up-line ml-1.5 text-xs"></i>
              </a>
              <Link
                to="/"
                className="px-6 py-3 rounded-full bg-background-100 text-foreground-900 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                Back to Home
                <i className="ri-arrow-left-line ml-1.5 text-xs"></i>
              </Link>
            </div>
          </section>

          {/* Internal Links */}
          <div className="border-t border-background-200 pt-8">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">Continue Reading</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link to="/superbuy-spreadsheet-guide" className="flex items-start gap-3 p-4 rounded-lg bg-background-100 hover:bg-background-200 transition-colors">
                <i className="ri-book-open-line text-accent-500 text-lg mt-0.5"></i>
                <div>
                  <span className="text-sm font-semibold text-foreground-900 block">Complete Beginner Guide</span>
                  <span className="text-xs text-foreground-500">Everything from first access to first order</span>
                </div>
              </Link>
              <Link to="/superbuy-spreadsheet-safety-guide" className="flex items-start gap-3 p-4 rounded-lg bg-background-100 hover:bg-background-200 transition-colors">
                <i className="ri-shield-check-line text-primary-500 text-lg mt-0.5"></i>
                <div>
                  <span className="text-sm font-semibold text-foreground-900 block">Safety Guide</span>
                  <span className="text-xs text-foreground-500">Payment protection, seller verification, and privacy</span>
                </div>
              </Link>
            </div>
          </div>

          <div className="text-center pt-4 pb-8">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors underline underline-offset-4">
              <i className="ri-arrow-left-line"></i>
              Back to Superbuy Spread Hub Home
            </Link>
          </div>
        </div>
      </article>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Superbuy Spreadsheet Shipping Guide: Costs, Carriers and Customs 2026",
          "description": "The definitive international shipping companion for Superbuy Spreadsheet shoppers. Compare carriers, estimate costs, navigate customs, consolidate orders, and choose the smartest route for your location from the US and UK to Europe, Australia, and beyond.",
          "image": "https://readdy.ai/api/search-image?query=Stacked%20denim%20jeans%20and%20colorful%20textured%20hoodies%20arranged%20in%20artistic%20flat%20lay%20on%20distressed%20wooden%20floor%2C%20warm%20natural%20sunlight%20from%20large%20window%2C%20rich%20indigo%20and%20terracotta%20and%20olive%20green%20tones%2C%20editorial%20catalog%20photography%20with%20deep%20shadows%20and%20texture%20emphasis%2C%20urban%20artisan%20workshop%20atmosphere%2C%20carefully%20curated%20casual%20fashion%20composition&width=600&height=800&seq=about-img-1-v2&orientation=portrait",
          "author": {
            "@type": "Organization",
            "name": "Superbuy Spread Hub"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Superbuy Spread Hub",
            "logo": {
              "@type": "ImageObject",
              "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67defc9f4dfae2c04004e8ea78b88107.png"
            }
          },
          "datePublished": "2026-07-13",
          "dateModified": "2026-07-13",
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": "https://superbuyspread.homes/superbuy-spreadsheet-shipping-guide"
          }
        }) }}
      />
    </div>
  );
}